import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useAuth } from '../auth/AuthContext';
import { apiGet, apiPost, apiUpload } from '../api/http';

export default function ProjectsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const completedImportInputRef = useRef(null);
  const [completedImportBusy, setCompletedImportBusy] = useState(false);
  const [completedImportMessage, setCompletedImportMessage] = useState('');

  // ---------------------------------------------------------------------------
  // "Edited / Updated" indicators
  //   - Edited (global): updated_at is meaningfully later than created_at
  //   - Updated (per-user): updated_at is newer than when this user last opened the project
  // ---------------------------------------------------------------------------
  const LS_LAST_SEEN_KEY = 'lp_proj_last_seen_v1';

  const safeParseMs = (v) => {
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  };

  const getCreatedAtMs = (p) =>
    safeParseMs(p?.created_at) ??
    safeParseMs(p?.createdAt) ??
    safeParseMs(p?.created_on) ??
    safeParseMs(p?.createdOn) ??
    null;

  const getUpdatedAtMs = (p) =>
    safeParseMs(p?.updated_at) ??
    safeParseMs(p?.updatedAt) ??
    safeParseMs(p?.modified_at) ??
    safeParseMs(p?.modifiedAt) ??
    safeParseMs(p?.last_modified_at) ??
    safeParseMs(p?.lastModifiedAt) ??
    null;

  const readLastSeenMap = () => {
    try {
      const raw = window.localStorage.getItem(LS_LAST_SEEN_KEY);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return obj && typeof obj === 'object' ? obj : {};
    } catch {
      return {};
    }
  };

  const writeLastSeen = (projectId, updatedAtMs) => {
    try {
      const obj = readLastSeenMap();
      obj[String(projectId)] = updatedAtMs || Date.now();
      window.localStorage.setItem(LS_LAST_SEEN_KEY, JSON.stringify(obj));
    } catch {
      // ignore storage errors
    }
  };

  const getBadges = (p) => {
    const createdMs = getCreatedAtMs(p);
    const updatedMs = getUpdatedAtMs(p);

    // Global "Edited" if updated is > created by at least 60 seconds (avoids DB default jitter)
    const editedGlobally =
      createdMs && updatedMs ? updatedMs - createdMs > 60_000 : false;

    // Per-user "Updated" if updated is newer than the last opened timestamp (stored as updatedAtMs)
    let updatedForMe = false;
    if (updatedMs) {
      const map = readLastSeenMap();
      const lastSeen = Number(map[String(p?.id)]);
      if (Number.isFinite(lastSeen)) updatedForMe = updatedMs > lastSeen;
    }

    return { editedGlobally, updatedForMe };
  };

  const renderNameWithBadges = (p) => {
    const name = pick(p, ['client_name', 'clientName', 'name'], '—');
    const { editedGlobally, updatedForMe } = getBadges(p);

    if (!editedGlobally && !updatedForMe) return name;

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Box>

        {editedGlobally && (
          <Chip
            label="Edited"
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: 12,
              borderColor: (t) =>
                alpha(t.palette.warning.main, t.palette.mode === 'dark' ? 0.55 : 0.7),
              color: (t) => t.palette.warning.main,
            }}
          />
        )}

        {updatedForMe && (
          <Chip
            label="Updated"
            size="small"
            variant="filled"
            sx={{
              height: 22,
              fontSize: 12,
              backgroundColor: (t) =>
                alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.22 : 0.18),
              color: (t) => t.palette.info.main,
            }}
          />
        )}
      </Stack>
    );
  };

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const initialShowCompleted =
    query.get('showCompleted') === '1' || query.get('showCompleted') === 'true';

  const initialTabFromQuery = query.get('tab');
  const initialTab = (() => {
    if (initialTabFromQuery === 'active' || initialTabFromQuery === 'closed' || initialTabFromQuery === 'deleted') {
      return initialTabFromQuery;
    }
    return initialShowCompleted ? 'closed' : 'active';
  })();

  const [tab, setTab] = useState(initialTab);

  // Keep tab valid if user isn't admin (no deleted tab)
  useEffect(() => {
    if (!isAdmin && tab === 'deleted') setTab('active');
  }, [isAdmin, tab]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/projects');
      if (Array.isArray(data)) {
        const norm = (s) => (s ?? '').toString().trim().toLowerCase();
        setProjects(data.filter((p) => norm(p.status) === 'active'));
        setCompleted(data.filter((p) => norm(p.status) === 'closed'));
        setDeleted(data.filter((p) => norm(p.status) === 'deleted'));
      } else {
        setProjects([]);
        setCompleted([]);
        setDeleted([]);
      }
      setLastRefreshed(new Date());
    } catch {
      setProjects([]);
      setCompleted([]);
      setDeleted([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Sync tab into URL (single source of truth) — NO eslint-disable needed
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const currentTab = sp.get('tab') || '';
    const tabWanted = tab;

    // If user isn't admin, never allow deleted in URL
    const safeTab = !isAdmin && tabWanted === 'deleted' ? 'active' : tabWanted;

    sp.set('tab', safeTab);
    sp.delete('showCompleted');

    const nextSearch = `?${sp.toString()}`;

    if (currentTab !== safeTab || location.search !== nextSearch) {
      navigate({ search: sp.toString() }, { replace: true });
    }
  }, [tab, isAdmin, location.search, navigate]);

  const softDeleteProject = useCallback(
    async (projectId) => {
      if (!isAdmin) return;
      // eslint-disable-next-line no-alert
      const okConfirm = window.confirm(
        'Soft delete this project? You can restore it later from the Deleted tab.'
      );
      if (!okConfirm) return;

      try {
        await apiPost(`/api/projects/${projectId}/soft-delete`, {});
        await loadProjects();
      } catch {
        // eslint-disable-next-line no-alert
        window.alert('Failed to delete project.');
      }
    },
    [isAdmin, loadProjects]
  );

  const restoreProject = useCallback(
    async (projectId) => {
      if (!isAdmin) return;
      try {
        await apiPost(`/api/projects/${projectId}/restore`, {});
        await loadProjects();
      } catch {
        // eslint-disable-next-line no-alert
        window.alert('Failed to restore project.');
      }
    },
    [isAdmin, loadProjects]
  );

  const importCompletedProjects = useCallback(
    async (file) => {
      if (!isAdmin || !file) return;
      setCompletedImportBusy(true);
      setCompletedImportMessage('');
      try {
        const formData = new FormData();
        formData.append('file', file);
        const result = await apiUpload('/api/projects/import-completed', formData);
        const count = Number(result?.imported || 0);
        setCompletedImportMessage(`${count} completed project${count === 1 ? '' : 's'} imported successfully.`);
        await loadProjects();
        setTab('closed');
      } catch (err) {
        setCompletedImportMessage(`Import failed: ${err.message}`);
      } finally {
        setCompletedImportBusy(false);
        if (completedImportInputRef.current) completedImportInputRef.current.value = '';
      }
    },
    [isAdmin, loadProjects]
  );

  function formatTime(dt) {
    try {
      return new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(dt);
    } catch {
      return dt?.toLocaleString?.() ?? '';
    }
  }

  function statusChip(statusRaw) {
    const status = (statusRaw ?? '').toString().trim().toLowerCase();

    if (status === 'active') {
      return <Chip label="Active" size="small" color="success" variant="outlined" />;
    }
    if (status === 'deleted') {
      return <Chip label="Deleted" size="small" color="error" variant="outlined" />;
    }
    if (status.includes('query') || status.includes('review') || status.includes('pending')) {
      return <Chip label={statusRaw} size="small" color="warning" variant="outlined" />;
    }
    if (status.includes('closed') || status.includes('completed') || status.includes('inactive')) {
      return <Chip label={statusRaw} size="small" color="default" variant="outlined" />;
    }
    return <Chip label={statusRaw || '—'} size="small" color="info" variant="outlined" />;
  }

  // -----------------------------
  // Completed tab: rich list UI
  // -----------------------------
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState('disbursement_date');
  const [sortDir, setSortDir] = useState('desc');
  const [groupBy, setGroupBy] = useState('none');

  // Keep these stable across potential API field naming differences
  const pick = useCallback((obj, keys, fallback = '') => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && `${v}`.trim() !== '') return v;
    }
    return fallback;
  }, []);

  const toNumber = useCallback((v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const s = `${v}`.replace(/,/g, '').trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }, []);

  const toDate = useCallback((v) => {
    if (!v) return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isFinite(d?.getTime?.()) ? d : null;
  }, []);

  const formatMoney = useCallback(
    (v) => {
      const n = toNumber(v);
      if (n === null) return '—';
      try {
        return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
      } catch {
        return `${Math.round(n)}`;
      }
    },
    [toNumber]
  );

  const formatPct = useCallback(
    (v) => {
      const n = toNumber(v);
      if (n === null) return '—';
      // If the API stores 0.085 vs 8.5, try a safe heuristic
      const pct = n > 1.5 ? n : n * 100;
      return `${pct.toFixed(pct < 10 ? 2 : 1)}%`;
    },
    [toNumber]
  );

  const formatTenure = useCallback(
    (p) => {
      const tenureText = pick(p, ['tenure_text', 'tenureText'], '');
      if (tenureText) return tenureText;

      const tenureYears = toNumber(pick(p, ['tenure_years', 'tenureYears', 'tenure_year']));
      const tenureMonths = toNumber(pick(p, ['tenure_months', 'tenureMonths', 'tenure_month']));

      if (tenureYears !== null && tenureYears !== 0) return `${tenureYears} yr`;
      if (tenureMonths !== null && tenureMonths !== 0) return `${tenureMonths} mo`;
      return '—';
    },
    [pick, toNumber]
  );

  const completedRowsRaw = useMemo(() => (Array.isArray(completed) ? completed : []), [completed]);

  const completedRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const filtered = !q
      ? completedRowsRaw
      : completedRowsRaw.filter((p) => {
          const id = `${pick(p, ['id'], '')}`.toLowerCase();
          const client = `${pick(p, ['client_name', 'clientName', 'name'], '')}`.toLowerCase();
          const bank = `${pick(p, ['bank_name', 'bankName'], '')}`.toLowerCase();
          const workedBy = `${pick(p, ['worked_by', 'workedBy', 'assignee', 'assigned_to', 'assignedTo'], '')}`.toLowerCase();
          const loanType = `${pick(p, ['loan_type', 'loanType'], '')}`.toLowerCase();
          const product = `${pick(p, ['product', 'product_name', 'productName'], '')}`.toLowerCase();
          return (
            id.includes(q) ||
            client.includes(q) ||
            bank.includes(q) ||
            workedBy.includes(q) ||
            loanType.includes(q) ||
            product.includes(q)
          );
        });

    const dir = sortDir === 'asc' ? 1 : -1;
    const key = sortKey;

    const valueForSort = (p) => {
      if (key === 'id') return `${pick(p, ['id'], '')}`;
      if (key === 'application_number') return `${pick(p, ['application_number', 'applicationNumber'], '')}`;
      if (key === 'client_name') return `${pick(p, ['client_name', 'clientName', 'name'], '')}`;
      if (key === 'loan_type') return `${pick(p, ['loan_type', 'loanType'], '')}`;
      if (key === 'bank_name') return `${pick(p, ['bank_name', 'bankName'], '')}`;
      if (key === 'product') return `${pick(p, ['product', 'product_name', 'productName'], '')}`;
      if (key === 'worked_by')
        return `${pick(p, ['worked_by', 'workedBy', 'assignee', 'assigned_to', 'assignedTo'], '')}`;
      if (key === 'loan_amount') return toNumber(pick(p, ['loan_amount', 'loanAmount', 'amount'], null));
      if (key === 'roi') return toNumber(pick(p, ['roi', 'interest_rate', 'interestRate'], null));
      if (key === 'ltv') return toNumber(pick(p, ['ltv'], null));
      if (key === 'tat') return toNumber(pick(p, ['tat', 'tat_days', 'tatDays'], null));
      if (key === 'import_source') return `${pick(p, ['import_source_type', 'importSourceType'], '')}`;
      if (key === 'disbursement_date')
        return toDate(pick(p, ['disbursement_date', 'disbursementDate', 'closed_at', 'closedAt'], null));
      return `${pick(p, [key], '')}`;
    };

    const cmp = (a, b) => {
      const va = valueForSort(a);
      const vb = valueForSort(b);

      // Nulls always go last
      if (va === null || va === undefined || va === '') return 1;
      if (vb === null || vb === undefined || vb === '') return -1;

      if (va instanceof Date && vb instanceof Date) return (va.getTime() - vb.getTime()) * dir;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return `${va}`.localeCompare(`${vb}`, undefined, { sensitivity: 'base', numeric: true }) * dir;
    };

    return [...filtered].sort(cmp);
  }, [completedRowsRaw, pick, searchText, sortDir, sortKey, toDate, toNumber]);

  const groupedCompleted = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'All', rows: completedRows }];

    const keyForGroup = (p) => {
      if (groupBy === 'bank_name') return `${pick(p, ['bank_name', 'bankName'], '—')}`.trim() || '—';
      if (groupBy === 'worked_by')
        return `${pick(p, ['worked_by', 'workedBy', 'assignee', 'assigned_to', 'assignedTo'], '—')}`.trim() || '—';
      if (groupBy === 'tat') {
        const tat = toNumber(pick(p, ['tat', 'tat_days', 'tatDays'], null));
        if (tat === null) return 'TAT: —';
        if (tat <= 2) return 'TAT: 0–2 days';
        if (tat <= 5) return 'TAT: 3–5 days';
        if (tat <= 10) return 'TAT: 6–10 days';
        return 'TAT: 10+ days';
      }
      return '—';
    };

    const map = new Map();
    for (const r of completedRows) {
      const k = keyForGroup(r);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }

    const out = Array.from(map.entries())
      .map(([k, rows]) => ({ key: k, rows }))
      .sort((a, b) => `${a.key}`.localeCompare(`${b.key}`, undefined, { sensitivity: 'base', numeric: true }));

    return out.length ? out : [{ key: 'All', rows: completedRows }];
  }, [completedRows, groupBy, pick, toNumber]);

  const completedColumns = useMemo(
    () => [
      { key: 'id', label: 'ID', align: 'left', width: 80 },
      { key: 'application_number', label: 'App. No.', align: 'left', width: 120 },
      { key: 'client_name', label: 'Client Name', align: 'left' },
      { key: 'loan_type', label: 'Loan Type', align: 'left', width: 120 },
      { key: 'bank_name', label: 'Bank Name', align: 'left', width: 160 },
      { key: 'loan_amount', label: 'Loan Amount', align: 'right', width: 140 },
      { key: 'tenure', label: 'Tenure', align: 'left', width: 90 },
      { key: 'roi', label: 'ROI', align: 'right', width: 90 },
      { key: 'ltv', label: 'LTV', align: 'right', width: 90 },
      { key: 'product', label: 'Product', align: 'left', width: 140 },
      { key: 'disbursement_date', label: 'Disb. Date', align: 'left', width: 120 },
      { key: 'worked_by', label: 'Worked By', align: 'left', width: 140 },
      { key: 'tat', label: 'TAT', align: 'right', width: 80 },
      { key: 'import_source', label: 'Source', align: 'left', width: 100 },
      { key: 'actions', label: '', align: 'right', width: 120 },
    ],
    []
  );

  const toggleSort = useCallback(
    (k) => {
      if (!k || k === 'actions' || k === 'tenure') return;
      if (sortKey !== k) {
        setSortKey(k);
        setSortDir(k === 'disbursement_date' ? 'desc' : 'asc');
      } else {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      }
    },
    [sortKey]
  );

  const renderCompletedCell = useCallback(
    (p, colKey) => {
      switch (colKey) {
        case 'id':
          return p.id;
        case 'application_number':
          return pick(p, ['application_number', 'applicationNumber'], '—');
        case 'client_name':
          return renderNameWithBadges(p);
        case 'loan_type':
          return pick(p, ['loan_type', 'loanType'], '—');
        case 'bank_name':
          return pick(p, ['bank_name', 'bankName'], '—');
        case 'loan_amount':
          return formatMoney(pick(p, ['loan_amount', 'loanAmount', 'amount'], null));
        case 'tenure':
          return formatTenure(p);
        case 'roi':
          return formatPct(pick(p, ['roi', 'interest_rate', 'interestRate'], null));
        case 'ltv':
          return formatPct(pick(p, ['ltv'], null));
        case 'product':
          return pick(p, ['product', 'product_name', 'productName'], '—');
        case 'disbursement_date': {
          const d = toDate(pick(p, ['disbursement_date', 'disbursementDate', 'closed_at', 'closedAt'], null));
          return d ? d.toLocaleDateString() : '—';
        }
        case 'worked_by':
          return pick(p, ['worked_by', 'workedBy', 'assignee', 'assigned_to', 'assignedTo'], '—');
        case 'tat': {
          const tat = toNumber(pick(p, ['tat', 'tat_days', 'tatDays'], null));
          return tat === null ? '—' : `${tat}`;
        }
        case 'import_source': {
          const source = String(pick(p, ['import_source_type', 'importSourceType'], ''));
          if (source === 'EMPLOYEE') return 'Employee';
          if (source === 'DIRECT') return 'Direct';
          return '—';
        }
        default:
          return '—';
      }
    },
    [formatMoney, formatPct, formatTenure, pick, toDate, toNumber]
  );

  // FIX: define these (they were missing earlier and caused blank screen)
  const showCompleted = tab === 'closed';
  const showDeleted = tab === 'deleted';
  const list = showDeleted ? deleted : showCompleted ? completed : projects;
  const rows = list;

  return (
    <Box
      sx={{
        width: '100%',
        // Only the Completed tab should use full screen width
        maxWidth: showCompleted ? 'none' : 1100,
        mx: showCompleted ? 0 : 'auto',
        // Add page padding only when we go full width so content doesn't stick to edges
        px: showCompleted ? { xs: 2, md: 3 } : 0,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.25 }}>
            {tab === 'active' && 'Active Projects'}
            {tab === 'closed' && 'Completed Projects'}
            {tab === 'deleted' && 'Deleted Projects'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {showDeleted
              ? 'Deleted projects (admins can restore).'
              : showCompleted
              ? 'Completed/closed projects across the organization.'
              : 'Active loan cases currently in progress.'}
            {lastRefreshed ? `  •  Last refreshed: ${formatTime(lastRefreshed)}` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
          <Button variant="outlined" onClick={loadProjects} disabled={loading}>
            Refresh
          </Button>

          {isAdmin && (
            <Button variant="contained" onClick={() => navigate('/project/new')}>
              + Add New Project
            </Button>
          )}
        </Stack>
      </Stack>

      <Box sx={{ mb: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 42,
            '& .MuiTab-root': { minHeight: 42, fontWeight: 900 },
          }}
        >
          <Tab value="active" label={`Active (${projects.length})`} />
          <Tab value="closed" label={`Completed (${completed.length})`} />
          {isAdmin && <Tab value="deleted" label={`Deleted (${deleted.length})`} />}
        </Tabs>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {showCompleted ? (
          <Box sx={{ p: 2 }}>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.75,
                mb: 2,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.6),
                background:
                  theme.palette.mode === 'dark'
                    ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(
                        theme.palette.background.paper,
                        0.65
                      )})`
                    : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(
                        theme.palette.background.paper,
                        0.85
                      )})`,
              })}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <TextField
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  label="Search (name / bank / person / loan type / product / ID)"
                  placeholder="Type to filter…"
                  sx={{ flex: 1, minWidth: 260 }}
                />

                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel id="completed-sort-label">Sort</InputLabel>
                  <Select
                    labelId="completed-sort-label"
                    value={`${sortKey}:${sortDir}`}
                    label="Sort"
                    onChange={(e) => {
                      const [k, d] = `${e.target.value}`.split(':');
                      setSortKey(k);
                      setSortDir(d);
                    }}
                  >
                    <MenuItem value="disbursement_date:desc">Disbursement: New → Old</MenuItem>
                    <MenuItem value="disbursement_date:asc">Disbursement: Old → New</MenuItem>
                    <Divider />
                    <MenuItem value="loan_amount:desc">Loan Amount: High → Low</MenuItem>
                    <MenuItem value="loan_amount:asc">Loan Amount: Low → High</MenuItem>
                    <Divider />
                    <MenuItem value="roi:desc">ROI: High → Low</MenuItem>
                    <MenuItem value="roi:asc">ROI: Low → High</MenuItem>
                    <Divider />
                    <MenuItem value="ltv:desc">LTV: High → Low</MenuItem>
                    <MenuItem value="ltv:asc">LTV: Low → High</MenuItem>
                    <Divider />
                    <MenuItem value="tat:asc">TAT: Low → High</MenuItem>
                    <MenuItem value="tat:desc">TAT: High → Low</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel id="completed-group-label">Group by</InputLabel>
                  <Select
                    labelId="completed-group-label"
                    value={groupBy}
                    label="Group by"
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <MenuItem value="none">No grouping</MenuItem>
                    <MenuItem value="bank_name">Bank Name</MenuItem>
                    <MenuItem value="worked_by">Person (Worked By)</MenuItem>
                    <MenuItem value="tat">TAT bucket</MenuItem>
                  </Select>
                </FormControl>

                {isAdmin && (
                  <>
                    <input
                      ref={completedImportInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={(event) => importCompletedProjects(event.target.files?.[0])}
                    />
                    <Button
                      variant="contained"
                      disabled={completedImportBusy}
                      onClick={() => completedImportInputRef.current?.click()}
                    >
                      {completedImportBusy ? 'Importing…' : 'Import Completed Excel'}
                    </Button>
                  </>
                )}

                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchText('');
                    setSortKey('disbursement_date');
                    setSortDir('desc');
                    setGroupBy('none');
                    setCompletedImportMessage('');
                  }}
                >
                  Reset
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Showing: ${completedRows.length}`} color="primary" variant="outlined" />
                {searchText.trim() ? (
                  <Chip
                    size="small"
                    label={`Filter: “${searchText.trim()}”`}
                    onDelete={() => setSearchText('')}
                    variant="outlined"
                  />
                ) : null}
                {groupBy !== 'none' ? (
                  <Chip size="small" label={`Grouped by: ${groupBy.replace('_', ' ')}`} variant="outlined" />
                ) : null}
                {completedImportMessage ? (
                  <Chip
                    size="small"
                    label={completedImportMessage}
                    color={completedImportMessage.startsWith('Import failed') ? 'error' : 'success'}
                    variant="outlined"
                    onDelete={() => setCompletedImportMessage('')}
                  />
                ) : null}
              </Stack>
            </Paper>

            {loading ? (
              <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>Loading…</Box>
            ) : completedRows.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>No completed projects</Box>
            ) : (
              <Stack spacing={1.25}>
                {groupedCompleted.map((g, idx) => (
                  <Accordion
                    key={`${g.key}-${idx}`}
                    defaultExpanded={groupBy === 'none' || groupedCompleted.length <= 3}
                    disableGutters
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        bgcolor: 'action.hover',
                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                      }}
                    >
                      <Typography sx={{ fontWeight: 900 }}>{g.key}</Typography>
                      <Chip size="small" label={`${g.rows.length}`} />
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Table size="small" aria-label="completed projects">
                        <TableHead>
                          <TableRow
                            sx={{
                              bgcolor: alpha('#000', 0.02),
                              '& th': { fontWeight: 900, color: 'text.secondary', whiteSpace: 'nowrap' },
                            }}
                          >
                            {completedColumns.map((c) => (
                              <TableCell
                                key={c.key}
                                align={c.align}
                                sx={{ width: c.width || 'auto', maxWidth: c.width || 'none' }}
                              >
                                {c.key === 'actions' || c.key === 'tenure' ? (
                                  c.label
                                ) : (
                                  <TableSortLabel
                                    active={sortKey === c.key}
                                    direction={sortKey === c.key ? sortDir : 'asc'}
                                    onClick={() => toggleSort(c.key)}
                                  >
                                    {c.label}
                                  </TableSortLabel>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {g.rows.map((p) => (
                            <TableRow key={p.id} hover>
                              {completedColumns.map((c) => (
                                <TableCell
                                  key={`${p.id}-${c.key}`}
                                  align={c.align}
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    maxWidth: c.width || 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontFamily:
                                      c.key === 'id'
                                        ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                                        : 'inherit',
                                    fontWeight: c.key === 'client_name' ? 700 : 400,
                                  }}
                                >
                                  {c.key === 'actions' ? (
                                    <Button
                                      component={RouterLink}
                                      to={`/project/${p.id}`}
                                      onClick={() => writeLastSeen(p.id, getUpdatedAtMs(p) || Date.now())}
                                      variant="outlined"
                                      size="small"
                                      sx={(theme) => ({
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        borderWidth: 1.5,
                                        color:
                                          theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        borderColor:
                                          theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.primary.light, 0.75)
                                            : alpha(theme.palette.primary.main, 0.6),
                                        '&:hover': {
                                          borderWidth: 1.5,
                                          borderColor:
                                            theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.primary.light, 0.95)
                                              : alpha(theme.palette.primary.main, 0.85),
                                          backgroundColor: alpha(
                                            theme.palette.mode === 'dark'
                                              ? theme.palette.primary.light
                                              : theme.palette.primary.main,
                                            theme.palette.mode === 'dark' ? 0.12 : 0.08
                                          ),
                                        },
                                      })}
                                    >
                                      Open
                                    </Button>
                                  ) : (
                                    renderCompletedCell(p, c.key)
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
          </Box>
        ) : (
          <Table size="small" aria-label="projects table">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'action.hover',
                  '& th': { fontWeight: 900, color: 'text.secondary' },
                }}
              >
                <TableCell width={80}>ID</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell width={120}>Loan Type</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell width={250} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    {showDeleted
                      ? 'No deleted projects'
                      : showCompleted
                      ? 'No completed projects'
                      : 'No active projects'}
                  </TableCell>
                </TableRow>
              ) : (
                list.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell
                      sx={{
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      }}
                    >
                      {p.id}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>{p.client_name}</TableCell>
                    <TableCell>{p.loan_type}</TableCell>
                    <TableCell>{statusChip(p.status)}</TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          component={RouterLink}
                          to={`/project/${p.id}`}
                                      onClick={() => writeLastSeen(p.id, getUpdatedAtMs(p) || Date.now())}
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={(theme) => ({
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 1.25,
                            borderWidth: 1.5,
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            borderColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.light, 0.75)
                                : alpha(theme.palette.primary.main, 0.6),
                            '&:hover': {
                              borderWidth: 1.5,
                              borderColor:
                                theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.light, 0.95)
                                  : alpha(theme.palette.primary.main, 0.85),
                              backgroundColor: alpha(
                                theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.12 : 0.08
                              ),
                            },
                          })}
                        >
                          Open
                        </Button>

                        {isAdmin && !showDeleted && (
                          <Tooltip title="Soft delete (can be restored from Deleted tab)">
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => softDeleteProject(p.id)}
                            >
                              Delete
                            </Button>
                          </Tooltip>
                        )}

                        {showDeleted && isAdmin && (
                          <Tooltip title="Restore this project">
                            <Button
                              variant="outlined"
                              color="success"
                              size="small"
                              onClick={() => restoreProject(p.id)}
                            >
                              Restore
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
